import { Form } from 'antd';
import { notification } from 'components';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { useTypedSelector } from 'store/store';
import { ErrorMappingValueType } from 'typings';
import { useClientContactInfoUpdateMutation } from 'api';
import { groupContactInfoByType } from 'utils/contactUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  resetContactInfoFields,
  resetEditId,
  resetIsChanged,
  resetOptionType,
} from 'components/common/ContactFieldManager/contactFieldManagerSlice';
import { ContactInfoGrouped } from 'feature/clientTabs/general/contactInfo/useContactInfo';

export const useContactInfoEdit = (props: { onModalStateChange: () => void; modalState: boolean }) => {
  const { onModalStateChange, modalState } = props;
  const contacts = useTypedSelector((state) => state.contactFieldManager.contactInfoFields);
  const isChanged = useTypedSelector((state) => state.contactFieldManager.isChanged);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const dispatch = useAppDispatch();
  const [updateClientContactInfo, { isLoading }] = useClientContactInfoUpdateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );
  const contactInfoToUpdate: ContactInfoGrouped = useMemo(() => groupContactInfoByType(contacts), [contacts]);
  const onValuesChange = useCallback(() => setDisableSaveBtn(false), []);

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setDisableSaveBtn(true);
    dispatch(resetEditId());
    dispatch(resetIsChanged());
    dispatch(resetOptionType());
    dispatch(resetContactInfoFields());
  }, [form, onModalStateChange, dispatch]);

  const onContactInfoUpdate = useCallback(async () => {
    const payload: any = {};
    let contactData = contactInfoToUpdate;
    if (!contacts.length) {
      try {
        await form.validateFields();
        contactData = groupContactInfoByType([form.getFieldsValue()]);
      } catch {
        return;
      }
    }
    payload.clientId = id as string;
    payload.contactInfo = contactData;
    try {
      const { data, error } = await updateClientContactInfo(payload);

      if (data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'Contact Info was successfully updated.',
        });
        onCancel();
      } else {
        handleErrors(error);
      }
    } catch (err) {
      handleErrors(err);
    }
  }, [
    contactInfoToUpdate,
    id,
    openNotificationWithIcon,
    updateClientContactInfo,
    contacts.length,
    form,
    handleErrors,
    onCancel,
  ]);

  useEffect(() => {
    if (modalState && isChanged) {
      setDisableSaveBtn(false);
    }
  }, [isChanged, modalState]);

  return {
    onContactInfoUpdate,
    onCancel,
    onValuesChange,
    contextHolder,
    form,
    disableSaveBtn,
    isLoading,
  };
};
